import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input, Modal } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Eye, EyeOff } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { GraphicArea } from '@/common/components/GraphicArea'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { formatDate } from '@/common/utils/formatDate'
import { useEditPassword } from './hooks/useEditPassword'
import {
  ProfilePasswordForm,
  ProfilePasswordSchema,
} from './schemas/ProfilePasswordSchema'

export default function Password() {
  const [isEditing, setIsEditing] = useState(false)
  const [showActualPassword, setShowActualPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const { userInfo } = useGetUserInfo()
  const { t } = useTranslation(['profile', 'common'])
  const { handleEditPassword, isPendingEditPassword } = useEditPassword()
  const hookForm = useForm<ProfilePasswordForm>({
    resolver: zodResolver(ProfilePasswordSchema()),
  })

  const handleToggleShowActualPassword = () =>
    setShowActualPassword(!showActualPassword)

  const handleToggleShowNewPassword = () => setShowNewPassword(!showNewPassword)

  const handleToggleShowConfirmNewPassword = () =>
    setShowConfirmNewPassword(!showConfirmNewPassword)

  const handleChangePassword = async (data: ProfilePasswordForm) => {
    const payload = {
      currentPassword: data?.currentPassword,
      newPassword: data?.newPassword,
    }
    await handleEditPassword(payload)
    setIsEditing(false)
    hookForm.reset()
    setShowActualPassword(false)
    setShowNewPassword(false)
    setShowConfirmNewPassword(false)
    window.HSOverlay.close(document.getElementById('change-password')!)
  }

  return (
    <>
      <HeadProvider>
        <Title>{t('profile:password.browser.title')}</Title>
      </HeadProvider>

      <GraphicArea.Root>
        <FormProvider {...hookForm}>
          <GraphicArea.Header>
            <h3 className="text-2xl font-semibold text-sky-800">
              {t('password.title')}
            </h3>
          </GraphicArea.Header>

          <GraphicArea.Body>
            <form className="flex flex-col gap-6 h-[90%] justify-between">
              <div className="max-h-full p-2 overflow-y-auto">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <Input.Icon
                      disabled={!isEditing}
                      placeholder="**************"
                      iconPosition="right"
                      icon={
                        <button
                          type="button"
                          onClick={
                            isEditing
                              ? handleToggleShowActualPassword
                              : undefined
                          }
                        >
                          {showActualPassword ? (
                            <Eye size={16} />
                          ) : (
                            <EyeOff size={16} />
                          )}
                        </button>
                      }
                      name="currentPassword"
                      label={t('password.current_password.label')}
                      type={showActualPassword ? 'text' : 'password'}
                    />

                    {userInfo && (
                      <div className="flex items-center gap-2 justify-between w-fit">
                        <span className="font-inter font-light text-sm text-base-500">{`${t('password.updated_info.last_change')}:`}</span>

                        <span className="font-inter font-light text-sm text-base-500">
                          {userInfo?.lastPasswordChangeAt &&
                            formatDate(
                              new Date(userInfo?.lastPasswordChangeAt),
                              `dd '${t('password.last_changed.label')}'	MMMM '${t('password.last_changed.label')}' yyyy`,
                            )}
                        </span>
                      </div>
                    )}
                  </div>

                  {isEditing && (
                    <>
                      <Input.Icon
                        disabled={!isEditing}
                        placeholder="**************"
                        iconPosition="right"
                        icon={
                          <button
                            type="button"
                            onClick={handleToggleShowNewPassword}
                          >
                            {showNewPassword ? (
                              <Eye size={16} />
                            ) : (
                              <EyeOff size={16} />
                            )}
                          </button>
                        }
                        label={t('password.new_password.label')}
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                      />

                      <Input.Icon
                        disabled={!isEditing}
                        placeholder="**************"
                        iconPosition="right"
                        label={t('password.confirm_new_password.label')}
                        icon={
                          <button
                            type="button"
                            onClick={handleToggleShowConfirmNewPassword}
                          >
                            {showConfirmNewPassword ? (
                              <Eye size={16} />
                            ) : (
                              <EyeOff size={16} />
                            )}
                          </button>
                        }
                        name="confirmNewPassword"
                        type={showConfirmNewPassword ? 'text' : 'password'}
                      />
                    </>
                  )}
                </div>
              </div>
            </form>
          </GraphicArea.Body>

          <GraphicArea.Footer className="flex flex-row justify-end">
            {isEditing && (
              <div className="flex flex-row w-full justify-end gap-2">
                <Button.Root
                  type="button"
                  variant="secondary"
                  disabled={isPendingEditPassword}
                  onClick={() => {
                    setIsEditing(!isEditing)
                    hookForm.reset()
                  }}
                >
                  {t('actions.cancel_button.text')}
                </Button.Root>

                <Button.Root
                  onClick={hookForm.handleSubmit(() =>
                    window.HSOverlay.open(
                      document.getElementById('change-password')!,
                    ),
                  )}
                >
                  {t('actions.save_button.text')}
                </Button.Root>
              </div>
            )}

            {!isEditing && (
              <Button.Root type="button" onClick={() => setIsEditing(true)}>
                {t('actions.edit_button.text')}
              </Button.Root>
            )}
          </GraphicArea.Footer>

          <Modal.Root clickOutside id="change-password">
            <Modal.Header type={'warning'}>
              <Modal.Icon type="warning" />

              <h1 className="text-lg font-semibold">
                {t('password.modal.change_password.title')}
              </h1>
            </Modal.Header>
            <Modal.Body>
              <div className="flex w-full items-center justify-center">
                <p className="mt-1 text-gray-800">
                  {t('password.modal.change_password.description')}
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="w-full flex items-center justify-center my-3 gap-4">
                <Button.Root
                  color="brand"
                  type="button"
                  onClick={() =>
                    window.HSOverlay.close(
                      document.getElementById('change-password')!,
                    )
                  }
                  variant="secondary"
                  data-hs-overlay={`#change-password`}
                >
                  {t(
                    'password.modal.change_password.actions.cancel_button.text',
                  )}
                </Button.Root>

                <Button.Root
                  type="button"
                  onClick={() => handleChangePassword(hookForm.getValues())}
                  variant="primary"
                >
                  {t(
                    'password.modal.change_password.actions.confirm_button.text',
                  )}
                </Button.Root>
              </div>
            </Modal.Footer>
          </Modal.Root>
        </FormProvider>
      </GraphicArea.Root>
    </>
  )
}
